import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Users,
    Folder,
    Calendar,
    BookUser,
    Box,
    Receipt,
    BarChart,
    Lock,
    Share2,
    Wrench,
    ChevronRight,
    ChevronDown,
    Search,
    Calculator,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MenuItem {
    key: string;
    label: string;
    icon: React.ElementType;
    path?: string;
    children?: { label: string; path: string }[];
}

interface DespachosSidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
}

const menuConfig: MenuItem[] = [
    {
        key: "clientes",
        label: "Clientes",
        icon: Users,
        path: "/dashboard/despachos/clientes",
    },
    // {
    //     key: "busqueda_judicial",
    //     label: "Búsqueda Judicial",
    //     icon: Search,
    //     path: "/dashboard/despachos/busqueda-judicial/samai",
    // },
    {
        key: "calculadoras",
        label: "Calculadoras",
        icon: Calculator,
        path: "/dashboard/despachos/calculadoras",
    },
    {
        key: "procesos",
        label: "Procesos",
        icon: Folder,
        children: [
            { label: "Procesos", path: "/dashboard/despachos/procesos" },
            { label: "Trámites", path: "/dashboard/despachos/tramites" },
            // { label: "Búsqueda Judicial", path: "/dashboard/despachos/busqueda-judicial" },
            // { label: "Búsqueda Judicial", path: "/dashboard/despachos/busqueda-judicial/samai" },
        ],
    },
    {
        key: "calendario",
        label: "Calendario y Tareas",
        icon: Calendar,
        children: [
            { label: "Calendario", path: "/dashboard/despachos/calendario" },
            { label: "Lista de tareas", path: "/dashboard/despachos/tareas" },
        ],
    },
    {
        key: "contactos",
        label: "Contactos",
        icon: BookUser,
        children: [
            { label: "Contactos", path: "/dashboard/despachos/contactos" },
            { label: "Enviar email", path: "/dashboard/despachos/enviar-email" },
        ],
    },
    {
        key: "archivos",
        label: "Archivos",
        icon: Box,
        children: [
            { label: "Archivos del Despacho", path: "/dashboard/despachos/archivos" },
            { label: "Mis archivos", path: "/dashboard/despachos/mis-archivos" },
        ],
    },
    {
        key: "recibos",
        label: "Recibos",
        icon: Receipt,
        children: [
            { label: "Listado Recibos", path: "/dashboard/despachos/recibos" },
            { label: "Crear Recibo Manual", path: "/dashboard/despachos/crear-recibo" },
        ],
    },
    // {
    //     key: "graficos",
    //     label: "Gráficos",
    //     icon: BarChart,
    //     children: [
    //         { label: "Procesos", path: "/dashboard/despachos/graficos/procesos" },
    //         { label: "Trámites", path: "/dashboard/despachos/graficos/tramites" },
    //         { label: "Servicios", path: "/dashboard/despachos/graficos/servicios" },
    //         { label: "Recibos", path: "/dashboard/despachos/graficos/recibos" },
    //         { label: "Clientes", path: "/dashboard/despachos/graficos/clientes" },
    //         { label: "Tareas", path: "/dashboard/despachos/graficos/tareas" },
    //     ],
    // },
    // {
    //     key: "lepd",
    //     label: "LEPD",
    //     icon: Lock,
    //     path: "/dashboard/despachos/lepd",
    // },
    // {
    //     key: "recursos",
    //     label: "Recursos compartidos",
    //     icon: Share2,
    //     children: [
    //         { label: "Mis recursos compartidos", path: "/dashboard/despachos/mis-recursos" },
    //         { label: "Todos los recursos...", path: "/dashboard/despachos/todos-recursos" },
    //     ],
    // },
];

const DespachosSidebar = ({ isOpen = false, onClose }: DespachosSidebarProps) => {
    const location = useLocation();
    const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({
        procesos: true,
        calendario: false,
        contactos: false,
        archivos: false,
        recibos: false,
        recursos: false,
        graficos: false,
    });

    const toggleSubmenu = (key: string) => {
        setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const isPathActive = (path: string) => location.pathname === path;
    const isParentActive = (item: MenuItem) => {
        if (item.path) return location.pathname.includes(item.key);
        return item.children?.some(child => location.pathname === child.path);
    };

    return (
        <aside
            className={cn(
                "fixed inset-y-0 left-0 z-40 w-64 max-w-[85vw] bg-white border-r border-gray-100 h-full flex flex-col overflow-y-auto transition-transform duration-300 lg:relative lg:z-auto lg:w-60 lg:max-w-none lg:translate-x-0",
                isOpen ? "translate-x-0" : "-translate-x-full"
            )}
        >
            <nav className="flex-1 py-3 px-2 space-y-0.5">
                {menuConfig.map((item) => {
                    const Icon = item.icon;
                    const isActive = isParentActive(item);

                    if (item.path) {
                        // Simple link item
                        return (
                            <Link
                                key={item.key}
                                to={item.path}
                                onClick={onClose}
                                className={cn(
                                    "flex items-center w-full px-3 py-2.5 text-sm rounded-xl transition-all duration-200 group",
                                    isActive
                                        ? "bg-legal-blue text-white font-medium shadow-md shadow-legal-blue/15"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                )}
                            >
                                <Icon className={cn(
                                    "h-4 w-4 mr-3 transition-colors",
                                    isActive ? "text-white" : "text-gray-400 group-hover:text-gray-600"
                                )} />
                                <span>{item.label}</span>
                            </Link>
                        );
                    }

                    // Submenu item
                    const isOpen = openSubmenus[item.key];
                    return (
                        <div key={item.key}>
                            <button
                                onClick={() => toggleSubmenu(item.key)}
                                className={cn(
                                    "flex items-center justify-between w-full px-3 py-2.5 text-sm rounded-xl transition-all duration-200 group",
                                    isActive && !isOpen
                                        ? "bg-blue-50 text-legal-blue font-medium"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                )}
                            >
                                <div className="flex items-center">
                                    <Icon className={cn(
                                        "h-4 w-4 mr-3 transition-colors",
                                        isActive ? "text-legal-blue" : "text-gray-400 group-hover:text-gray-600"
                                    )} />
                                    <span>{item.label}</span>
                                </div>
                                <ChevronRight className={cn(
                                    "h-3.5 w-3.5 text-gray-300 transition-transform duration-200",
                                    isOpen && "rotate-90"
                                )} />
                            </button>
                            {isOpen && item.children && (
                                <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                                    {item.children.map((child) => (
                                        <Link
                                            key={child.path}
                                            to={child.path}
                                            onClick={onClose}
                                            className={cn(
                                                "flex items-center w-full px-3 py-2 text-sm rounded-lg transition-all duration-200",
                                                isPathActive(child.path)
                                                    ? "text-legal-blue font-medium bg-blue-50/60"
                                                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                                            )}
                                        >
                                            {child.label}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Configuración - separated 
                <div className="pt-3 mt-3 border-t border-gray-100">
                    <Link
                        to="/dashboard/despachos/configuracion"
                        className={cn(
                            "flex items-center w-full px-3 py-2.5 text-sm rounded-xl transition-all duration-200 group",
                            location.pathname.includes("/configuracion")
                                ? "bg-legal-blue text-white font-medium shadow-md shadow-legal-blue/15"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                    >
                        <Wrench className={cn(
                            "h-4 w-4 mr-3 transition-colors",
                            location.pathname.includes("/configuracion") ? "text-white" : "text-gray-400 group-hover:text-gray-600"
                        )} />
                        <span>Configuración</span>
                    </Link>
                </div>*/}
            </nav>
        </aside>
    );
};

export default DespachosSidebar;
